/**
 * Centralized permission checking utilities
 * Consolidates all user role and access control checks
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * User role types supported by the system
 */
export type UserRole = "admin" | "document_uploader" | "user";

/**
 * Permission check result with optional error details
 */
export interface PermissionCheckResult {
  hasPermission: boolean;
  error?: string;
}

/**
 * Checks if the current user has admin role
 * 
 * @returns Promise<boolean> - True if user is an admin, false otherwise
 * 
 * @example
 * const isAdmin = await checkIsAdmin();
 * if (isAdmin) {
 *   // Show admin controls
 * }
 */
export async function checkIsAdmin(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .single();

    return !error && !!data;
  } catch {
    return false;
  }
}

/**
 * Checks if the current user can manage documents (admin or document_uploader role)
 * 
 * @returns Promise<boolean> - True if user can manage documents, false otherwise
 * 
 * @example
 * const canManage = await checkCanManageDocuments();
 * if (canManage) {
 *   // Show document upload interface
 * }
 */
export async function checkCanManageDocuments(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .in("role", ["admin", "document_uploader"])
      .maybeSingle();

    return !error && !!data;
  } catch {
    return false;
  }
}

/**
 * Checks if the current user has a specific role
 * 
 * @param role - The role to check for
 * @returns Promise<boolean> - True if user has the specified role, false otherwise
 * 
 * @example
 * const hasRole = await checkHasRole("document_uploader");
 */
export async function checkHasRole(role: UserRole): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", role)
      .single();

    return !error && !!data;
  } catch {
    return false;
  }
}

/**
 * Checks if the current user has any of the specified roles
 * 
 * @param roles - Array of roles to check
 * @returns Promise<boolean> - True if user has any of the specified roles
 * 
 * @example
 * const hasAnyRole = await checkHasAnyRole(["admin", "document_uploader"]);
 */
export async function checkHasAnyRole(roles: UserRole[]): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .in("role", roles)
      .maybeSingle();

    return !error && !!data;
  } catch {
    return false;
  }
}

/**
 * Gets all roles for the current user
 * 
 * @returns Promise<UserRole[]> - Array of user's roles
 * 
 * @example
 * const roles = await getUserRoles();
 * if (roles.includes("admin")) {
 *   // User is admin
 * }
 */
export async function getUserRoles(): Promise<UserRole[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);

    if (error || !data) return [];
    return data.map(row => row.role as UserRole);
  } catch {
    return [];
  }
}

/**
 * Checks if current user can access a specific document
 * 
 * @param documentId - The ID of the document to check access for
 * @returns Promise<PermissionCheckResult> - Permission check result with details
 * 
 * @example
 * const result = await checkCanAccessDocument(docId);
 * if (result.hasPermission) {
 *   // Load document
 * } else {
 *   console.error(result.error);
 * }
 */
export async function checkCanAccessDocument(documentId: string): Promise<PermissionCheckResult> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { hasPermission: false, error: "User not authenticated" };
    }

    // Check if user is admin (full access)
    const isAdmin = await checkIsAdmin();
    if (isAdmin) {
      return { hasPermission: true };
    }

    // Check if document exists and is ready
    const { data: doc, error: docError } = await supabase
      .from("permanent_documents")
      .select("id, status")
      .eq("id", documentId)
      .single();

    if (docError || !doc) {
      return { hasPermission: false, error: "Document not found" };
    }

    if (doc.status !== "ready") {
      return { hasPermission: false, error: "Document not ready" };
    }

    // Check team access
    const { data: teamAccess, error: teamError } = await supabase
      .from("team_document_access")
      .select(`
        team_id,
        teams!inner(
          team_members!inner(user_id)
        )
      `)
      .eq("document_id", documentId);

    if (!teamError && teamAccess && teamAccess.length > 0) {
      const hasTeamAccess = teamAccess.some(access => 
        access.teams?.team_members?.some((member: any) => member.user_id === user.id)
      );
      
      if (hasTeamAccess) {
        return { hasPermission: true };
      }
    }

    // Default: document is accessible to all authenticated users
    return { hasPermission: true };
  } catch (error) {
    console.error("Error checking document access:", error);
    return { hasPermission: false, error: "Error checking permissions" };
  }
}
